import React from "react";
import { Text, plainText, safeElement, TEXT_LEVELS, type TextLevel } from "../../atoms/text/text";
import {
  ChannelLogo,
  type ChannelLogoShape,
  type ChannelLogoSize,
  type ChannelLogoSurface
} from "./channel-logo";
import { TooltipTrigger } from "./indicator";
import { isSafeImageSrc } from "../../services/safe-image-src";
import { LocalityBadge, type LocalityKind } from "./locality-badge";
import { TooltipScope } from "./tooltip-scope.client";
import styles from "./logo-grid.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * A wrapping grid of logos — a Genre Pack's Subscriptions row (GP5) and its Channels row (GP6).
 * Server Component.
 */

/**
 * GP5 / GP6 — Figma draws a pack's two rows as two different components: `SVOD App`, a square app
 * tile, and `Logo container`, a circular disc, at different sizes and on different fills.
 */
export const LOGO_GRID_VARIANTS = ["default", "genre-channel", "genre-myfree", "genre-subscription"] as const;
export type LogoGridVariant = (typeof LOGO_GRID_VARIANTS)[number];

const TILE: Record<
  LogoGridVariant,
  { shape: ChannelLogoShape; size: ChannelLogoSize; surface: ChannelLogoSurface }
> = {
  default: {
    shape: "circle",
    size: "medium",
    surface: "tertiary"
  },
  "genre-channel": {
    shape: "circle",
    size: "genre",
    surface: "genre"
  },
  /* The MyFree pack's channel discs: light on mobile (`16545:21285`), as `genre-channel` above. */
  "genre-myfree": {
    shape: "circle",
    size: "genre",
    surface: "genre-myfree"
  },
  "genre-subscription": {
    shape: "square",
    size: "genre",
    surface: "artwork"
  }
};

export interface LogoGridItem {
  /** Stable identity — a channels-service CCID or an Epoch plan code. Never
   *  the name: two channels can share a name AND a number. */
  id: string;
  /** The tooltip and the tile's accessible name. Empty renders neither. */
  name: string;
  logoUrl?: string;
  /**
   * GP7 — "local channels and RSNs in a pack show a Local, RSN or National badge". Absent draws no
   * badge, which is every national channel and every subscription; `localityKindFor()`
   * (`locality-badge.tsx`) is the one place that decision is taken.
   */
  badgeKind?: LocalityKind;
  /** GP7 — content-managed badge wording. Absent takes the kind's default. */
  badgeLabel?: string;
  /**
   * GP7 — "the badge has an ⓘ tooltip when there is tooltip text, and no ⓘ otherwise". `undefined`
   * takes the kind's default wording; only an empty string suppresses the ⓘ.
   */
  badgeTooltip?: string;
}

export interface LogoGridProps {
  items: LogoGridItem[];
  /**
   * GP5 / GP6 — content-managed ("Subscriptions", "Channels"). The row is unlabelled when it is not
   * written, which is what those criteria ask for, so this is not a missing-value case.
   */
  heading?: string;
  /** AX5 — the pack titles are headings, so these rank under them. */
  headingLevel?: TextLevel;
  /**
   * GP5 — a subscription with no logo shows its name in the tile rather than the "no logo" image.
   * Channels (GP6) keep the image, which is the default.
   */
  nameFallback?: boolean;
  /**
   * Which of Figma's two row components this row is. Unset draws the 48px circle on the tertiary
   * fill, which is what a row with no frame behind it keeps.
   */
  variant?: LogoGridVariant;
  /** Layout only. */
  className?: string;
}

function LogoGridImpl({
  items,
  heading,
  headingLevel = "h4",
  nameFallback = false,
  variant = "default",
  className
}: LogoGridProps) {
  /* GP6 — the row shows only when the heading is written AND there is
     something in it; GP15 hides a pack with nothing to show. */
  if (items.length === 0) {
    return null;
  }

  const level = safeElement(headingLevel, "h4", TEXT_LEVELS);

  return (
    <TooltipScope>
      <div className={[styles.row, className].filter(Boolean).join(" ")}>
        {heading && (
          <Text as={level} className={styles.heading} variant="body-1" weight="medium">
            {heading}
          </Text>
        )}
        <ul className={styles.grid}>
          {items.map(item => (
            <li className={styles.cell} key={item.id}>
              <LogoGridTile item={item} nameFallback={nameFallback} variant={variant} />
            </li>
          ))}
        </ul>
      </div>
    </TooltipScope>
  );
}
export const LogoGrid = withSafeRender(LogoGridImpl, "LogoGrid");

/**
 * One tile. Split out because the two shapes it takes — a named tile that is a tooltip trigger, and
 * an unnamed one that is inert — are a branch, and a branch inside a `map` is where the wrong one
 * quietly ships.
 */
function LogoGridTile({
  item,
  nameFallback,
  variant
}: {
  item: LogoGridItem;
  nameFallback: boolean;
  variant: LogoGridVariant;
}) {
  const tile = TILE[variant] ?? TILE.default;
  // GR9 — markup in a label never shows as formatting or as visible tags.
  const name = plainText(item.name);
  /* GP5's name fallback has to stand in the same footprint as the tile it replaces — that is the
     whole reason it exists — so it follows the variant, not a fixed 48px disc. */
  const nameTileClass = [
    styles.nameTile,
    variant === "genre-subscription" ? styles.nameTileGenreSubscription : undefined
  ]
    .filter(Boolean)
    .join(" ");
  const badge = item.badgeKind && (
    <LocalityBadge
      className={styles.badge}
      kind={item.badgeKind}
      label={item.badgeLabel}
      tooltip={item.badgeTooltip}
    />
  );

  const logo = (
    <ChannelLogo
      className={styles.tile}
      logoUrl={item.logoUrl}
      shape={tile.shape}
      size={tile.size}
      surface={tile.surface}
    />
  );

  if (!name) {
    return (
      <>
        {logo}
        {badge}
      </>
    );
  }

  return (
    <>
      <TooltipTrigger content={name} label={name}>
        {/* The same gate `ChannelLogo` rejects on, not truthiness. */}
        {nameFallback && !isSafeImageSrc(item.logoUrl) ?
          <Text as="span" className={nameTileClass} variant="label">
            {name}
          </Text> :
          logo}
      </TooltipTrigger>
      {badge}
    </>
  );
}

export default LogoGrid;
